import React from 'react'
import { IconAlertTriangle, IconExclamationCircle } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Banner } from '@/components/banners/Banner'
import { Card } from '@/components/cards/Card'
import { Progress } from '@/components/progress/Progress'

import { USAGE_STATE, getUsageState, getUsagePercent, getBannerCopy } from './helpers'

const formatNumber = (value) => (value ?? 0).toLocaleString()

/**
 * @param {number} used Interactions used in the contract year.
 * @param {number} limit Contracted interaction limit.
 * @param {string} [contractWindow] Pre-formatted contract year, e.g.
 *   "6/15/2026 – 6/14/2027". Built by `formatContractWindow`.
 */
export const UsageProgressCard = ({
  used,
  limit,
  contractWindow,
  onHowItWorksClick,
  loading = false,
}) => {
  const state = getUsageState(used, limit)
  const pct = getUsagePercent(used, limit)
  const bannerCopy = getBannerCopy(state, limit)
  const bannerIcon =
    state === USAGE_STATE.EXCEEDED ? <IconExclamationCircle /> : <IconAlertTriangle />

  return (
    <Card>
      {/* Skip the banner while loading — its threshold depends on used/limit
          that we don't have yet, and a flash of "approaching" or "exceeded"
          mid-fetch would be alarming. */}
      {!loading && bannerCopy && (
        <Banner
          dataTestId="omnichannel-usage-banner"
          warning={state === USAGE_STATE.APPROACHING}
          critical={state === USAGE_STATE.EXCEEDED}
          icon={bannerIcon}
        >
          {bannerCopy}
        </Banner>
      )}

      <header className="omnichannel-usage__progress-header">
        <div
          className="omnichannel-usage__progress-headline"
          data-testid="omnichannel-usage-headline"
        >
          {loading ? (
            <Loader size="small" data-testid="omnichannel-usage-headline-loader" />
          ) : (
            <>
              <span className="omnichannel-usage__progress-headline-used">
                {formatNumber(used)}
              </span>
              <span className="omnichannel-usage__progress-headline-divider"> / </span>
              <span className="omnichannel-usage__progress-headline-limit">
                {formatNumber(limit)} interactions
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          className="omnichannel-usage__how-link"
          onClick={onHowItWorksClick}
          data-testid="omnichannel-usage-how-link"
        >
          How usage works?
        </button>
      </header>

      <Progress
        // Hold the bar at 0 (no warning/critical class) while loading so it
        // doesn't paint a stale or alarming threshold color from the previous
        // render before the new numbers land.
        value={loading ? 0 : Math.min(pct, 1) * 100}
        warning={!loading && state === USAGE_STATE.APPROACHING}
        critical={!loading && state === USAGE_STATE.EXCEEDED}
        aria-label="Omnichannel interaction usage"
        aria-busy={loading || undefined}
        data-testid="omnichannel-usage-progress"
      />

      <footer className="omnichannel-usage__progress-footer">
        <span className="omnichannel-usage__progress-footer-left">
          {!loading && contractWindow && <>Contract year {contractWindow}</>}
        </span>
        <span
          className="omnichannel-usage__progress-footer-percent"
          data-testid="omnichannel-usage-percent"
        >
          {loading ? (
            <Loader size="small" data-testid="omnichannel-usage-percent-loader" />
          ) : (
            `${(pct * 100).toFixed(1)}%`
          )}
        </span>
      </footer>
    </Card>
  )
}
